import React, { useState } from 'react';
import axios from 'axios';
import { X, Users, Send, Loader2, CheckCircle2 } from 'lucide-react';
import { API } from '../api';
import useAntiSpam from '../hooks/useAntiSpam';

const GROUP_TYPES = [
  { key: 'corporate', label: 'Corporate' },
  { key: 'family',    label: 'Family' },
  { key: 'school',    label: 'School' },
  { key: 'wedding',   label: 'Wedding' },
  { key: 'friends',   label: 'Friends' },
  { key: 'other',     label: 'Other' },
];

const SERVICES = ['Flights', 'Hotels', 'Meals', 'Sightseeing', 'Transfers', 'Guide'];

const empty = {
  name: '', phone: '', email: '', company: '',
  destination: '', travel_from: '', travel_to: '',
  pax_adults: 15, pax_children: 0,
  group_type: 'corporate',
  budget_per_person: 0,
  inclusions_needed: ['Flights', 'Hotels'],
  message: '',
};

const GroupBookingModal = ({ open, onClose }) => {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);
  const { fields: antiSpamFields, honeypotInput, readHoneypot } = useAntiSpam();

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleService = (s) => {
    setForm((f) => {
      const cur = new Set(f.inclusions_needed || []);
      if (cur.has(s)) cur.delete(s); else cur.add(s);
      return { ...f, inclusions_needed: Array.from(cur) };
    });
  };

  const submit = async () => {
    setErr('');
    if (!form.name.trim()) return setErr('Please enter your name');
    if (!form.phone.trim()) return setErr('Please enter your phone number');
    if (Number(form.pax_adults) < 5) return setErr('Group bookings need at least 5 travellers');
    setSubmitting(true);
    try {
      await axios.post(`${API}/group-enquiries`, {
        ...form,
        pax_adults: Number(form.pax_adults) || 0,
        pax_children: Number(form.pax_children) || 0,
        budget_per_person: Number(form.budget_per_person) || 0,
        ...antiSpamFields,
        website: readHoneypot(),
      });
      setDone(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not submit — please try again');
    } finally { setSubmitting(false); }
  };

  const close = () => { setDone(false); setErr(''); setForm(empty); onClose && onClose(); };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center overflow-y-auto p-3 sm:p-4"
      data-testid="group-booking-modal"
      onClick={close}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md my-4 max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-[#0d64a8] to-[#0a2a4a] text-white rounded-t-xl shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <Users size={18} className="shrink-0"/>
            <div className="min-w-0">
              <h2 className="text-sm font-bold leading-tight truncate">Group Booking Enquiry</h2>
              <p className="text-[10px] opacity-90 leading-tight">Custom quote in 24 hrs · min. 5 pax</p>
            </div>
          </div>
          <button onClick={close} className="text-white/80 hover:text-white shrink-0" aria-label="Close" data-testid="group-modal-close"><X size={18} /></button>
        </div>

        {done ? (
          <div className="p-6 text-center" data-testid="group-modal-success">
            <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-2">
              <CheckCircle2 size={24} />
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-1">Thanks — enquiry received!</h3>
            <p className="text-xs text-gray-600">Our specialist will call <b>{form.phone}</b> within 24 hours.</p>
            <button onClick={close} className="mt-4 bg-[#0d64a8] hover:bg-[#0a548e] text-white text-sm px-5 py-1.5 rounded-md">Done</button>
          </div>
        ) : (
          <>
            {/* Scrollable body — stays under viewport height */}
            <div className="p-4 space-y-3 overflow-y-auto flex-1">
              {honeypotInput}
              {/* Contact */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-0.5">Name *</label>
                  <input value={form.name} onChange={(e) => upd('name', e.target.value)} placeholder="Full name" className="w-full border border-gray-200 rounded px-2.5 py-1.5 text-xs focus:border-[#0d64a8]" data-testid="group-name-input"/>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-0.5">Phone *</label>
                  <input value={form.phone} onChange={(e) => upd('phone', e.target.value)} placeholder="+91 98…" className="w-full border border-gray-200 rounded px-2.5 py-1.5 text-xs focus:border-[#0d64a8]" data-testid="group-phone-input"/>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-0.5">Email</label>
                  <input type="email" value={form.email} onChange={(e) => upd('email', e.target.value)} placeholder="you@example.com" className="w-full border border-gray-200 rounded px-2.5 py-1.5 text-xs focus:border-[#0d64a8]" data-testid="group-email-input"/>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-0.5">Company</label>
                  <input value={form.company} onChange={(e) => upd('company', e.target.value)} placeholder="Optional" className="w-full border border-gray-200 rounded px-2.5 py-1.5 text-xs focus:border-[#0d64a8]"/>
                </div>
              </div>

              {/* Trip */}
              <div>
                <label className="block text-[11px] font-semibold text-gray-700 mb-0.5">Destination(s)</label>
                <input value={form.destination} onChange={(e) => upd('destination', e.target.value)} placeholder="e.g. Bali, Kerala…" className="w-full border border-gray-200 rounded px-2.5 py-1.5 text-xs focus:border-[#0d64a8]" data-testid="group-destination-input"/>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-0.5">From</label>
                  <input type="date" value={form.travel_from} onChange={(e) => upd('travel_from', e.target.value)} className="w-full border border-gray-200 rounded px-2 py-1.5 text-xs focus:border-[#0d64a8]"/>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-0.5">To</label>
                  <input type="date" value={form.travel_to} onChange={(e) => upd('travel_to', e.target.value)} className="w-full border border-gray-200 rounded px-2 py-1.5 text-xs focus:border-[#0d64a8]"/>
                </div>
              </div>

              {/* Pax + Budget */}
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-0.5">Adults *</label>
                  <input type="number" min={5} value={form.pax_adults} onChange={(e) => upd('pax_adults', e.target.value)} className="w-full border border-gray-200 rounded px-2 py-1.5 text-xs focus:border-[#0d64a8]" data-testid="group-adults-input"/>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-0.5">Children</label>
                  <input type="number" min={0} value={form.pax_children} onChange={(e) => upd('pax_children', e.target.value)} className="w-full border border-gray-200 rounded px-2 py-1.5 text-xs focus:border-[#0d64a8]"/>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-700 mb-0.5">Budget/pax</label>
                  <input type="number" min={0} value={form.budget_per_person} onChange={(e) => upd('budget_per_person', e.target.value)} placeholder="INR" className="w-full border border-gray-200 rounded px-2 py-1.5 text-xs focus:border-[#0d64a8]"/>
                </div>
              </div>

              {/* Group type */}
              <div>
                <label className="block text-[11px] font-semibold text-gray-700 mb-1">Group Type</label>
                <div className="flex flex-wrap gap-1">
                  {GROUP_TYPES.map((g) => (
                    <button
                      key={g.key}
                      type="button"
                      onClick={() => upd('group_type', g.key)}
                      data-testid={`group-type-${g.key}`}
                      className={`text-[11px] px-2.5 py-1 rounded-full border transition ${form.group_type === g.key ? 'bg-[#0d64a8] border-[#0d64a8] text-white' : 'bg-white border-gray-200 text-gray-700 hover:border-[#0d64a8]'}`}
                    >
                      {g.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Services */}
              <div>
                <label className="block text-[11px] font-semibold text-gray-700 mb-1">Services Needed</label>
                <div className="flex flex-wrap gap-1">
                  {SERVICES.map((s) => {
                    const active = (form.inclusions_needed || []).includes(s);
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => toggleService(s)}
                        data-testid={`group-svc-${s.toLowerCase()}`}
                        className={`text-[11px] px-2.5 py-1 rounded border transition ${active ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-white border-gray-200 text-gray-600 hover:border-[#0d64a8]'}`}
                      >
                        {active ? '✓ ' : ''}{s}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-[11px] font-semibold text-gray-700 mb-0.5">Notes</label>
                <textarea value={form.message} onChange={(e) => upd('message', e.target.value)} rows={2} placeholder="Preferences, dietary needs, meeting rooms, etc." className="w-full border border-gray-200 rounded px-2.5 py-1.5 text-xs focus:border-[#0d64a8] resize-none"/>
              </div>

              {err && <div className="bg-red-50 border border-red-100 text-red-700 text-xs px-2.5 py-1.5 rounded" data-testid="group-form-error">{err}</div>}
            </div>

            {/* Sticky footer */}
            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-100 shrink-0 bg-white rounded-b-xl">
              <button onClick={close} data-testid="group-cancel-btn" className="text-xs text-gray-600 hover:text-gray-900 px-3 py-1.5 border border-gray-200 rounded">Cancel</button>
              <button
                onClick={submit}
                disabled={submitting}
                data-testid="group-submit-btn"
                className="text-xs bg-[#0d64a8] hover:bg-[#0a548e] disabled:opacity-60 text-white px-4 py-1.5 rounded flex items-center gap-1.5"
              >
                {submitting ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                {submitting ? 'Sending…' : 'Send Enquiry'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default GroupBookingModal;
